/**
 * ScanList Component
 * Displays scans table with TGDataTable framework
 */

import React from 'react';
import { Tag, Badge, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { TGDataTable } from '../../../components/common';
import { StatusTag } from '../../../components/common';
import { colors } from '../../../theme/designTokens';

/**
 * Scan status tag renderer
 */
function ScanStatusTag({ status }) {
  const statusConfig = {
    completed: { color: colors.status.pass, icon: <CheckCircleOutlined />, label: 'Completed' },
    failed: { color: colors.status.fail, icon: <CloseCircleOutlined />, label: 'Failed' },
    running: { color: colors.status.running, icon: <ExclamationCircleOutlined />, label: 'Running' },
    pending: { color: colors.status.info, label: 'Pending' },
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Tag color={config.color} icon={config.icon}>
      {config.label}
    </Tag>
  );
}

/**
 * Trust Score renderer with color coding
 */
function TrustScoreDisplay({ score }) {
  let color = colors.severity.low;
  if (score >= 80) color = colors.status.pass;
  else if (score >= 60) color = colors.status.warning;
  else color = colors.status.fail;

  return (
    <span style={{ fontWeight: 600, color }}>
      {score?.toFixed(1) || 'N/A'}
    </span>
  );
}

/**
 * ScanList Component
 */
function ScanList({ data, loading, onViewDetails, onRefresh, showProjectColumn = false, toolbar, externalFilterCount, onClearExternalFilters }) {
  // Generate dynamic filters from data
  const trustScoreFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const scores = [...new Set(data.map(s => s.trust_score).filter(s => s != null))].sort((a, b) => b - a);
    return scores.map(score => ({ label: score.toFixed(1), value: score }));
  }, [data]);

  const startedByFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const users = [...new Set(data.map(s => s.started_by || 'System'))];
    return users.map(user => ({ label: user, value: user }));
  }, [data]);

  const controlsFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const counts = [...new Set(data.map(s => s.controls_scanned).filter(c => c != null))].sort((a, b) => a - b);
    return counts.map(count => ({ label: count.toString(), value: count }));
  }, [data]);

  const passCountFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const counts = [...new Set(data.map(s => s.pass_count).filter(c => c != null))].sort((a, b) => a - b);
    return counts.map(count => ({ label: count.toString(), value: count }));
  }, [data]);

  const failCountFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const counts = [...new Set(data.map(s => s.fail_count).filter(c => c != null))].sort((a, b) => a - b);
    return counts.map(count => ({ label: count.toString(), value: count }));
  }, [data]);

  const warningCountFilters = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    const counts = [...new Set(data.map(s => s.warning_count).filter(c => c != null))].sort((a, b) => a - b);
    return counts.map(count => ({ label: count.toString(), value: count }));
  }, [data]);

  const projectFilters = React.useMemo(() => {
    if (!data || data.length === 0 || !showProjectColumn) return [];
    const projects = [...new Set(data.map(s => s.project_name).filter(p => p))].sort();
    return projects.map(project => ({ label: project, value: project }));
  }, [data, showProjectColumn]);

  const baseColumns = [
    {
      key: 'id',
      title: 'ID',
      dataIndex: 'id',
      width: 80,
      fixed: 'left',
      render: (id) => <span style={{ fontFamily: 'Monaco, monospace', fontSize: 11 }}>#{id}</span>,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'trust_score',
      title: 'Trust Score',
      dataIndex: 'trust_score',
      width: 120,
      align: 'center',
      render: (score) => <TrustScoreDisplay score={score} />,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: trustScoreFilters,
    },
    {
      key: 'controls_scanned',
      title: 'Controls',
      dataIndex: 'controls_scanned',
      width: 100,
      align: 'center',
      render: (count) => <Badge count={count} showZero color={colors.primary[500]} />,
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'pass_count',
      title: 'Pass',
      dataIndex: 'pass_count',
      width: 80,
      align: 'center',
      render: (count, record) => (
        <Tooltip title={count > 0 ? `Click to view ${count} passed controls` : `${count} controls passed`}>
          <Tag
            color={colors.status.pass}
            icon={<CheckCircleOutlined />}
            onClick={() => count > 0 && onViewDetails(record, 'pass')}
            style={{ cursor: count > 0 ? 'pointer' : 'default' }}
          >
            {count}
          </Tag>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'fail_count',
      title: 'Fail',
      dataIndex: 'fail_count',
      width: 80,
      align: 'center',
      render: (count, record) => (
        <Tooltip title={count > 0 ? `Click to view ${count} failed controls` : `${count} controls failed`}>
          <Tag
            color={count > 0 ? colors.status.fail : 'default'}
            icon={count > 0 ? <CloseCircleOutlined /> : null}
            onClick={() => count > 0 && onViewDetails(record, 'fail')}
            style={{ cursor: count > 0 ? 'pointer' : 'default' }}
          >
            {count}
          </Tag>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'warning_count',
      title: 'Warnings',
      dataIndex: 'warning_count',
      width: 100,
      align: 'center',
      render: (count, record) => (
        <Tooltip title={count > 0 ? `Click to view ${count} warning controls` : `${count} warnings`}>
          <Tag
            color={count > 0 ? colors.status.warning : 'default'}
            icon={count > 0 ? <ExclamationCircleOutlined /> : null}
            onClick={() => count > 0 && onViewDetails(record, 'warning')}
            style={{ cursor: count > 0 ? 'pointer' : 'default' }}
          >
            {count}
          </Tag>
        </Tooltip>
      ),
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'status',
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status) => <ScanStatusTag status={status} />,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Completed', value: 'completed' },
        { label: 'Failed', value: 'failed' },
        { label: 'Running', value: 'running' },
        { label: 'Pending', value: 'pending' },
      ],
    },
    {
      key: 'started_by',
      title: 'Started By',
      dataIndex: 'started_by',
      width: 150,
      render: (user) => user || 'System',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: startedByFilters,
    },
    {
      key: 'started_at',
      title: 'Started At',
      dataIndex: 'started_at',
      width: 180,
      render: (date) => (date ? new Date(date).toLocaleString() : 'N/A'),
      sortable: true,
    },
    {
      key: 'completed_at',
      title: 'Completed At',
      dataIndex: 'completed_at',
      width: 180,
      render: (date) => (date ? new Date(date).toLocaleString() : 'In Progress'),
      sortable: true,
    },
    {
      key: 'duration',
      title: 'Duration',
      dataIndex: 'duration_seconds',
      width: 120,
      render: (seconds, record) => {
        if (!seconds && record.started_at && record.completed_at) {
          const start = new Date(record.started_at);
          const end = new Date(record.completed_at);
          seconds = Math.floor((end - start) / 1000);
        }
        if (!seconds) return 'N/A';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
      },
      sortable: true,
      filterable: true,
      filterType: 'number',
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="View Details">
          <a onClick={() => onViewDetails(record)} style={{ color: colors.primary[500] }}>
            <EyeOutlined /> Details
          </a>
        </Tooltip>
      ),
    },
  ];

  // Project column (shown when viewing All Projects)
  const projectColumn = {
    key: 'project_name',
    title: 'Project',
    dataIndex: 'project_name',
    width: 150,
    render: (name) => name || '—',
    sortable: true,
    filterable: true,
    filterType: 'select',
    filterOptions: projectFilters,
  };

  // Build final columns array - insert project column after ID if showing all projects
  const columns = showProjectColumn
    ? [baseColumns[0], projectColumn, ...baseColumns.slice(1)]
    : baseColumns;

  return (
    <TGDataTable
      data={data}
      columns={columns}
      loading={loading}
      rowKey="id"
      pagination="client"
      pageSize={10}
      showExport
      showColumnConfig
      showFilters
      exportFilename="scans"
      presetKey="trustgrid.scans"
      onRefresh={onRefresh}
      toolbar={toolbar}
      externalFilterCount={externalFilterCount}
      onClearExternalFilters={onClearExternalFilters}
    />
  );
}

export default ScanList;
